import { type ClassValue, clsx } from 'clsx';
import { toast } from 'sonner';
import { twMerge } from 'tailwind-merge';
import { format, isValid, parseISO } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';

import { TransactionStatus } from '@/features/transaction/transaction.types';

import { type TransactionItem, ROUTE_BY_REF } from './constants/transaction-item';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const isValidEmail = (email: string): boolean => {
  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return regex.test(email);
};

const defaultErrorStyle = {
  background: '#ef4444',
  color: '#fff',
  borderColor: 'transparent',
};

const defaultSuccessStyle = {
  background: '#22c55e', // Tailwind green-500
  color: '#fff',
  borderColor: 'transparent',
};

export function showErrorToast(message: string, description?: string) {
  toast.error(message, {
    description,
    style: defaultErrorStyle,
  });
}

export function showSuccessToast(message: string, description?: string) {
  toast.success(message, {
    description,
    style: defaultSuccessStyle,
  });
}

export const formatDate = (date: string | Date, pattern: string): string => {
  if (!date) return '-';

  const parsed = typeof date === 'string' ? parseISO(date) : date;

  if (!isValid(parsed) || parsed.getFullYear() < 1800) {
    return '-';
  }

  const timeZone = 'Asia/Jakarta';
  const zonedDate = toZonedTime(parsed, timeZone);
  return format(zonedDate, pattern);
};

export const parseUrlDate = (val?: string): Date => {
  if (!val) return new Date();
  const parsed = parseISO(val);
  return isValid(parsed) ? parsed : new Date();
};

export const copyToClipboard = async (text: string): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
    return true;
  } catch (err) {
    console.error('Failed to copy: ', err);
    return false;
  }
};

export function cleanParams<T extends Record<string, unknown>>(params: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ''),
  ) as Partial<T>;
}

export const formatDateParam = (d?: Date) => (d ? format(d, 'yyyy-MM-dd') : undefined);

export const normalize = (v: string | undefined | null): string =>
  (v || '').toString().trim().toUpperCase();

export function resolveDetailPath(item: TransactionItem): string | null {
  const method = normalize(item.method);
  const txId = item.transaction_id;
  const refId = item.reference_id || item.merchant_reference;

  if (method && refId && ROUTE_BY_REF[method]) {
    return ROUTE_BY_REF[method](refId);
  }

  if (txId) {
    return `/transaction/${txId}`;
  }

  return null;
}

export const getColorChip = (status: TransactionStatus) => {
  switch (status) {
    case TransactionStatus.SUCCESS:
      return 'bg-success-light';
    case TransactionStatus.FAILED:
      return 'bg-error';
    case TransactionStatus.INPROGRESS:
      return 'bg-violet-400';
    case TransactionStatus.SETTLED:
      return 'bg-violet-400';
    case TransactionStatus.REVERSED:
      return 'bg-secondary';
    default:
      return 'bg-brand-blue';
  }
};

export const formatPhone = (phone?: string) => {
  if (!phone || phone.length <= 2) return phone ?? '';

  return (
    phone
      .replace(/\D/g, '') // Remove non-numeric characters
      .replace(/^(0+|62+)/, '') // Remove leading 0s or country code (62)
      .match(/.{1,4}/g) // Split into chunks of 4
      ?.join(' ')
      .trim() ?? ''
  );
};
